import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, formatApiErrorDetail } from "@/lib/api";
import { euro, dateIt, STATI } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { SelectCategoriaLavoro } from "@/components/SelectCategoriaLavoro";
import { Plus, Search, AlertTriangle, Trash2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { ConfermaEliminaCommessa } from "@/components/ConfermaEliminaCommessa";

const emptyForm = {
  cliente_id: "",
  descrizione: "",
  categoria_lavoro: "",
  data_apertura: "",
  data_inizio_lavori: "",
  data_fine_prevista: "",
  giorni_preavviso_scadenza: "7",
  stato: "aperta",
  importo_commessa: "",
  note: "",
  preventivo: {
    materiali_stimati: "",
    peso_materiali_stimato_kg: "",
    ore_previste: "",
    costo_orario_medio: "",
    trasferta_stimata: "",
    pasti_stimati: "",
    cene_stimate: "",
    veicoli_stimati: "",
  },
};

export default function Commesse() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { isAdmin } = useAuth();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("tutte");
  const [fCliente, setFCliente] = useState("tutti");
  const [fCategoria, setFCategoria] = useState("tutti");
  const [fDa, setFDa] = useState("");
  const [fA, setFA] = useState("");
  const [ordine, setOrdine] = useState("default");

  const { data: commesse = [], isLoading } = useQuery({
    queryKey: ["commesse"],
    queryFn: async () => (await api.get("/commesse")).data,
  });
  const { data: clienti = [] } = useQuery({
    queryKey: ["clienti"],
    queryFn: async () => (await api.get("/clienti")).data,
  });

  const [daEliminare, setDaEliminare] = useState(null);
  const [bloccoFatture, setBloccoFatture] = useState(null);
  const delMut = useMutation({
    mutationFn: async ({ cid, force }) =>
      (await api.delete(`/commesse/${cid}`, { params: force ? { force: true } : {} })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["commesse"] });
      setDaEliminare(null);
      setBloccoFatture(null);
      toast.success("Commessa spostata nel cestino");
    },
    onError: (e) => {
      if (e.response?.status === 409) setBloccoFatture(formatApiErrorDetail(e.response.data.detail));
      else toast.error(formatApiErrorDetail(e.response?.data?.detail));
    },
  });

  const createMut = useMutation({
    mutationFn: async (payload) => (await api.post("/commesse", payload)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["commesse"] });
      qc.invalidateQueries({ queryKey: ["dashboard-generale"] });
      toast.success("Commessa creata con successo");
      setOpen(false);
      setForm(emptyForm);
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  const submit = () => {
    if (!form.cliente_id || !form.descrizione) {
      toast.error("Seleziona un cliente e inserisci una descrizione");
      return;
    }
    if (!form.data_inizio_lavori || !form.data_fine_prevista) {
      toast.error("Inserisci Data inizio lavori e Data fine prevista");
      return;
    }
    if (form.data_fine_prevista < form.data_inizio_lavori) {
      toast.error("La data fine prevista non può precedere la data inizio lavori");
      return;
    }
    createMut.mutate({
      cliente_id: form.cliente_id,
      descrizione: form.descrizione,
      categoria_lavoro: form.categoria_lavoro || "",
      data_apertura: form.data_apertura || null,
      data_inizio_lavori: form.data_inizio_lavori,
      data_fine_prevista: form.data_fine_prevista,
      giorni_preavviso_scadenza: Math.max(0, parseInt(form.giorni_preavviso_scadenza, 10) || 0),
      stato: form.stato,
      importo_commessa: Number(form.importo_commessa) || 0,
      note: form.note,
      preventivo: {
        materiali_stimati: Number(form.preventivo.materiali_stimati) || 0,
        peso_materiali_stimato_kg: Number(form.preventivo.peso_materiali_stimato_kg) || 0,
        ore_previste: Number(form.preventivo.ore_previste) || 0,
        costo_orario_medio: Number(form.preventivo.costo_orario_medio) || 0,
        trasferta_stimata: Number(form.preventivo.trasferta_stimata) || 0,
        pasti_stimati: Number(form.preventivo.pasti_stimati) || 0,
        cene_stimate: Number(form.preventivo.cene_stimate) || 0,
        veicoli_stimati: Number(form.preventivo.veicoli_stimati) || 0,
      },
    });
  };

  const oggi = new Date().toISOString().slice(0, 10);
  const APERTE = ["aperta", "in_corso"];
  const scadenza = (c) => {
    if (!APERTE.includes(c.stato) || !c.data_fine_prevista) return null;
    const fine = c.data_fine_prevista.slice(0, 10);
    if (fine < oggi) return "scaduta";
    const giorni = Math.ceil((new Date(fine) - new Date(oggi)) / 86400000);
    return giorni <= (c.giorni_preavviso_scadenza ?? 7) ? "in_scadenza" : null;
  };
  const filtered = commesse
    .filter((c) => c.tipo !== "prestazione")
    .filter((c) => {
      const okFilter = filter === "tutte" || c.stato === filter;
      const okCliente = fCliente === "tutti" || c.cliente_id === fCliente;
      const okCategoria = fCategoria === "tutti" || (c.categoria_lavoro || "") === fCategoria;
      const ap = (c.data_apertura || "").slice(0, 10);
      const okPeriodo = (!fDa || ap >= fDa) && (!fA || ap <= fA);
      const okSearch =
        !search ||
        c.codice.toLowerCase().includes(search.toLowerCase()) ||
        c.descrizione.toLowerCase().includes(search.toLowerCase()) ||
        (c.cliente_nome || "").toLowerCase().includes(search.toLowerCase());
      return okFilter && okCliente && okCategoria && okPeriodo && okSearch;
    })
    .sort((a, b) => {
      if (ordine === "margine") return (Number(b.margine_perc) || -1e9) - (Number(a.margine_perc) || -1e9);
      const aA = APERTE.includes(a.stato), bA = APERTE.includes(b.stato);
      if (aA !== bA) return aA ? -1 : 1;
      if (aA && bA) return (a.data_fine_prevista || "9999").localeCompare(b.data_fine_prevista || "9999");
      return 0;
    });

  const setPrev = (k, v) =>
    setForm((f) => ({ ...f, preventivo: { ...f.preventivo, [k]: v } }));

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
            Commesse
          </h1>
          <p className="text-slate-500 mt-1">Gestisci i lavori, i preventivi e i costi.</p>
        </div>
        {(
          <Button
            onClick={() => setOpen(true)}
           
            data-testid="new-commessa-button"
          >
            <Plus className="h-4 w-4 mr-1.5" /> Nuova Commessa
          </Button>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Cerca per codice, cliente o descrizione…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 bg-white"
            data-testid="commesse-search"
          />
        </div>
        <div className="flex gap-1.5 flex-wrap">
          {["tutte", "aperta", "in_corso", "conclusa", "fatturata"].map((s) => (
            <button
              key={s}
              onClick={() => setFilter(s)}
              data-testid={`filter-${s}`}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                filter === s
                  ? "bg-primary text-white"
                  : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"
              }`}
            >
              {s === "tutte" ? "Tutte" : STATI[s].label}
            </button>
          ))}
        </div>
      </div>
      <div className="flex flex-wrap items-end gap-3" data-testid="commesse-filtri-extra">
        <div className="min-w-[220px]"><div className="text-[10px] uppercase text-slate-400 mb-1">Cliente</div>
          <SelectCategoriaLavoro tutte value={fCategoria} onChange={setFCategoria} className="bg-white" testid="commesse-filtro-categoria" />
          <Select value={fCliente} onValueChange={setFCliente}><SelectTrigger className="bg-white" data-testid="commesse-filtro-cliente"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="tutti">Tutti i clienti</SelectItem>{clienti.map((c) => <SelectItem key={c.id} value={c.id}>{c.ragione_sociale}</SelectItem>)}</SelectContent></Select></div>
        <div><div className="text-[10px] uppercase text-slate-400 mb-1">Apertura dal</div><Input type="date" value={fDa} onChange={(e) => setFDa(e.target.value)} className="bg-white" data-testid="commesse-filtro-da" /></div>
        <div><div className="text-[10px] uppercase text-slate-400 mb-1">al</div><Input type="date" value={fA} onChange={(e) => setFA(e.target.value)} className="bg-white" data-testid="commesse-filtro-a" /></div>
        {isAdmin && <div className="min-w-[200px]"><div className="text-[10px] uppercase text-slate-400 mb-1">Ordinamento</div>
          <Select value={ordine} onValueChange={setOrdine}><SelectTrigger className="bg-white" data-testid="commesse-ordine"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="default">Aperte prima, per scadenza</SelectItem><SelectItem value="margine">Margine % (dal più alto)</SelectItem></SelectContent></Select></div>}
        {(fCliente !== "tutti" || fDa || fA || ordine !== "default") && <Button variant="ghost" size="sm" onClick={() => { setFCliente("tutti"); setFDa(""); setFA(""); setOrdine("default"); }} data-testid="commesse-filtri-reset">Azzera filtri</Button>}
      </div>

      {isLoading ? (
        <div className="text-slate-400 py-16 text-center">Caricamento…</div>
      ) : filtered.length === 0 ? (
        <div className="text-slate-400 py-16 text-center border border-dashed border-slate-200 rounded-xl">
          Nessuna commessa trovata.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map((c) => { const sc = scadenza(c); return (
            <button
              key={c.id}
              onClick={() => navigate(`/commesse/${c.id}`)}
              data-testid={`commessa-card-${c.codice}`}
              data-scadenza={sc || "ok"}
              className={`text-left rounded-xl shadow-sm p-5 hover:shadow-md transition-all stagger-in border ${
                sc === "scaduta" ? "bg-red-50 border-red-300 hover:border-red-400" : sc === "in_scadenza" ? "bg-orange-50 border-orange-300 hover:border-orange-400" : "bg-white border-slate-200 hover:border-slate-300"
              }`}
            >
              <div className="flex items-center justify-between">
                <span className="font-mono text-xs text-slate-500">{c.codice}</span>
                <span
                  className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium border ${STATI[c.stato]?.cls}`}
                  data-testid={`status-badge-${c.stato}`}
                >
                  {STATI[c.stato]?.label}
                </span>
              </div>
              <h3 className="mt-2 font-semibold text-slate-900 leading-snug">
                {c.descrizione}
              </h3>
              <p className="text-sm text-slate-500">{c.cliente_nome}{c.categoria_lavoro && <span className="ml-2 text-[10px] uppercase tracking-wider rounded-full bg-slate-100 text-slate-600 px-2 py-0.5" data-testid={`commessa-cat-${c.id}`}>{c.categoria_lavoro}</span>}</p>
              {(
                <div className="mt-4 pt-3 border-t border-slate-100 grid grid-cols-3 gap-2 text-sm">
                  <div>
                    <div className="text-xs text-slate-400">Fatturato</div>
                    <div className="font-mono font-medium">{euro(c.importo_commessa)}</div>
                  </div>
                  <div>
                    <div className="text-xs text-slate-400">Da fatturare</div>
                    <div className={`font-mono font-medium ${c.da_fatturare > 0.005 ? "text-sky-700" : c.da_fatturare < -0.005 ? "text-red-600" : "text-slate-400"}`} data-testid={`commessa-da-fatturare-${c.id}`}>{euro(c.da_fatturare || 0)}</div>
                  </div>
                  <div>
                    <div className="text-xs text-slate-400">Margine</div>
                    <div
                      className={`font-mono font-semibold flex items-center gap-1 ${
                        c.margine < 0 ? "text-red-600" : "text-emerald-600"
                      }`}
                    >
                      {c.in_perdita && <AlertTriangle className="h-3.5 w-3.5" />}
                      {euro(c.margine)}
                    </div>
                  </div>
                </div>
              )}
              <div className="text-xs text-slate-400 mt-3 flex items-center justify-between">
                <span>
                  Apertura: {dateIt(c.data_apertura)}
                  {c.data_fine_prevista && (
                    <> · <span className={sc === "scaduta" ? "text-red-600 font-semibold" : sc === "in_scadenza" ? "text-orange-600 font-semibold" : ""} data-testid={`commessa-fine-prevista-${c.codice}`}>
                      Fine prevista: {dateIt(c.data_fine_prevista)}{sc === "scaduta" ? " · SCADUTA" : sc === "in_scadenza" ? " · in scadenza" : ""}
                    </span></>
                  )}
                </span>
                {isAdmin && (
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={(e) => { e.stopPropagation(); setDaEliminare(c); }}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); setDaEliminare(c); } }}
                    className="text-slate-300 hover:text-red-600 p-1 -m-1"
                    title="Elimina commessa"
                    data-testid={`elimina-commessa-${c.codice}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </span>
                )}
              </div>
            </button>
          ); })}
        </div>
      )}

      <ConfermaEliminaCommessa
        open={!!daEliminare}
        onOpenChange={(v) => { if (!v) { setDaEliminare(null); setBloccoFatture(null); } }}
        onConfirm={(force) => daEliminare && delMut.mutate({ cid: daEliminare.id, force })}
        loading={delMut.isPending}
        bloccoFatture={bloccoFatture}
      />

      {(
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display">Nuova Commessa</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Cliente *</Label>
              <Select
                value={form.cliente_id}
                onValueChange={(v) => setForm((f) => ({ ...f, cliente_id: v }))}
              >
                <SelectTrigger data-testid="commessa-cliente-select">
                  <SelectValue placeholder="Seleziona cliente" />
                </SelectTrigger>
                <SelectContent>
                  {clienti.map((cl) => (
                    <SelectItem key={cl.id} value={cl.id}>
                      {cl.ragione_sociale}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Descrizione del lavoro *</Label>
              <Textarea
                value={form.descrizione}
                onChange={(e) => setForm((f) => ({ ...f, descrizione: e.target.value }))}
                placeholder="Es. Prefabbricazione collettori INOX…"
                data-testid="commessa-descrizione-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Categoria lavoro</Label>
              <SelectCategoriaLavoro value={form.categoria_lavoro} onChange={(v) => setForm((f) => ({ ...f, categoria_lavoro: v }))} testid="commessa-categoria-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Stato</Label>
                <Select
                  value={form.stato}
                  onValueChange={(v) => setForm((f) => ({ ...f, stato: v }))}
                >
                  <SelectTrigger data-testid="commessa-stato-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(STATI).map(([k, v]) => (
                      <SelectItem key={k} value={k}>
                        {v.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Importo commessa (€)</Label>
                <Input
                  type="number"
                  value={form.importo_commessa}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, importo_commessa: e.target.value }))
                  }
                  data-testid="commessa-importo-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Data apertura</Label>
                <Input type="date" value={form.data_apertura} onChange={(e) => setForm((f) => ({ ...f, data_apertura: e.target.value }))} data-testid="commessa-apertura-input" />
              </div>
              <div className="space-y-1.5">
                <Label>Giorni preavviso scadenza</Label>
                <Input type="number" min="0" step="1" value={form.giorni_preavviso_scadenza} onChange={(e) => setForm((f) => ({ ...f, giorni_preavviso_scadenza: e.target.value }))} data-testid="commessa-preavviso-input" />
              </div>
              <div className="space-y-1.5">
                <Label>Data inizio lavori *</Label>
                <Input type="date" value={form.data_inizio_lavori} onChange={(e) => setForm((f) => ({ ...f, data_inizio_lavori: e.target.value }))} data-testid="commessa-inizio-lavori-input" />
              </div>
              <div className="space-y-1.5">
                <Label>Data fine prevista *</Label>
                <Input type="date" value={form.data_fine_prevista} min={form.data_inizio_lavori || undefined} onChange={(e) => setForm((f) => ({ ...f, data_fine_prevista: e.target.value }))} data-testid="commessa-fine-prevista-input" />
              </div>
            </div>

            <div className="pt-2 border-t border-slate-100">
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">
                Preventivo
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">Materiali stimati (€)</Label>
                  <Input
                    type="number"
                    value={form.preventivo.materiali_stimati}
                    onChange={(e) => setPrev("materiali_stimati", e.target.value)}
                    data-testid="preventivo-materiali-input"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Materiale previsto (kg)</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.1"
                    value={form.preventivo.peso_materiali_stimato_kg}
                    onChange={(e) => setPrev("peso_materiali_stimato_kg", e.target.value)}
                    data-testid="preventivo-peso-input"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Ore previste</Label>
                  <Input
                    type="number"
                    value={form.preventivo.ore_previste}
                    onChange={(e) => setPrev("ore_previste", e.target.value)}
                    data-testid="preventivo-ore-input"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Costo orario medio (€)</Label>
                  <Input
                    type="number"
                    value={form.preventivo.costo_orario_medio}
                    onChange={(e) => setPrev("costo_orario_medio", e.target.value)}
                    data-testid="preventivo-costo-orario-input"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Trasferte notti stimate (€)</Label>
                  <Input
                    type="number"
                    value={form.preventivo.trasferta_stimata}
                    onChange={(e) => setPrev("trasferta_stimata", e.target.value)}
                    data-testid="preventivo-trasferta-input"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Pasto cantiere stimato (€)</Label>
                  <Input
                    type="number"
                    value={form.preventivo.pasti_stimati}
                    onChange={(e) => setPrev("pasti_stimati", e.target.value)}
                    data-testid="preventivo-pasti-input"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Cena trasferta stimata (€)</Label>
                  <Input
                    type="number"
                    value={form.preventivo.cene_stimate}
                    onChange={(e) => setPrev("cene_stimate", e.target.value)}
                    data-testid="preventivo-cene-input"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Veicoli stimati (€)</Label>
                  <Input
                    type="number"
                    value={form.preventivo.veicoli_stimati}
                    onChange={(e) => setPrev("veicoli_stimati", e.target.value)}
                    data-testid="preventivo-veicoli-input"
                  />
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Annulla
            </Button>
            <Button
              onClick={submit}
              disabled={createMut.isPending}
             
              data-testid="commessa-save-button"
            >
              Crea Commessa
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      )}
    </div>
  );
}
